import { wpAPIget } from '../../util/wp-rest-api';
import LoadMore from '../archive-load-more';
import SiteEvents, { SiteEventNames } from '../../util/site-events';

class PostsList {
	/**
	 * PostsList constructor
	 *
	 * @param {HTMLElement} el The posts list element
	 */
	constructor(el) {
		this.el = el;
		this.cardsContainer = this.el.querySelector('[data-load-more-target]');
		this.loadMoreContainer = this.el.querySelector('.archive__load-more-container');
		this.noResultsContainer = this.el.querySelector('[data-no-results-container]');
		this.statusEl = this.el.querySelector('[data-posts-list-status]');
		this.loadingSpinner = this.el.querySelector('.posts-list__loader-container');
		this.statusAnnounceTimeout = null;

		this.endpoint = 'wp/v2/posts';
		// Sourced from the server-rendered `card_col_classes` (posts-list.php) so
		// cards added via "load more" always match the classes used on first load.
		this.cardContainerClasses =
			this.cardsContainer?.getAttribute('data-card-col-classes') || 'columns small-12 medium-6 large-3';
		this.postDisplayFunc = (cardMarkup, postsList) => `
			<div class="${postsList.cardContainerClasses}">
				${cardMarkup}
			</div>
		`;
		this.loadMore = this.loadMoreContainer ? new LoadMore(this.loadMoreContainer) : null;

		if (this.loadMore) {
			const curPage = parseInt(this.cardsContainer.getAttribute('data-current-page'), 10);
			const totalPages = parseInt(this.cardsContainer.getAttribute('data-total-pages'), 10);

			this.loadMore.setVisible(curPage < totalPages);
		}
	}

	/**
	 * Set the API endpoint used by this post list
	 *
	 * @param {string} endpoint
	 */
	setEndpoint(endpoint) {
		this.endpoint = endpoint;
	}

	/**
	 * Set the card container classes used when new cards are added dynamically
	 *
	 * @param {string} cardContainerClasses
	 */
	setCardContainerClasses(cardContainerClasses) {
		this.cardContainerClasses = cardContainerClasses;
	}

	/**
	 * Set the callback function used to generate markup for cards that are added
	 * dynamically
	 *
	 * @param {function} postDisplayFunc
	 */
	setPostDisplayCallback(postDisplayFunc) {
		this.postDisplayFunc = postDisplayFunc;
	}

	/**
	 * Set the loading spinner visibility
	 *
	 * @param {boolean} showSpinner
	 */
	showLoadingSpinner(showSpinner) {
		if (!this.loadingSpinner) {
			return;
		}

		if (showSpinner) {
			this.loadingSpinner.classList.remove('hide');
		} else {
			this.loadingSpinner.classList.add('hide');
		}
	}

	/**
	 * Clear the cards container
	 */
	clearCardsContainer() {
		this.cardsContainer.innerHTML = '';

		this.cardsContainer.setAttribute('data-current-page', 0);
		this.cardsContainer.setAttribute('data-total-pages', 1);

		this.showLoadingSpinner(true);

		this.loadMore.setVisible(false);
	}

	/**
	 * Set the visibility of the "no results" container and message
	 *
	 * @param {boolean} show
	 */
	showNoResultsMessage(show) {
		if (!this.noResultsContainer) {
			return;
		}

		if (show) {
			this.noResultsContainer.classList.remove('hide');
		} else {
			this.noResultsContainer.classList.add('hide');
		}
	}

	/**
	 * Announce a status message to assistive technology (WCAG 4.1.3)
	 *
	 * @param {string} message
	 */
	announceStatus(message) {
		if (!this.statusEl || !message) {
			return;
		}

		if (this.statusAnnounceTimeout) {
			window.clearTimeout(this.statusAnnounceTimeout);
		}

		// Clear first so identical messages are re-announced.
		this.statusEl.textContent = '';

		this.statusAnnounceTimeout = window.setTimeout(() => {
			this.statusEl.textContent = message;
		}, 50);
	}

	/**
	 * Build the status message for an AJAX listing update
	 *
	 * @param {number} loadedCount Number of items returned in this request
	 * @param {number} curPage Page before this request (0 = fresh filter/search)
	 * @param {number|null} totalResults Total matching results from API headers
	 * @returns {string}
	 */
	getStatusMessage(loadedCount, curPage, totalResults) {
		if (!loadedCount && curPage === 0) {
			const noResultsText = this.noResultsContainer?.querySelector('h4')?.textContent?.trim();

			return noResultsText || 'No results found.';
		}

		if (curPage === 0) {
			if (totalResults !== null && !Number.isNaN(totalResults)) {
				return totalResults === 1 ? '1 result found.' : `${totalResults} results found.`;
			}

			return loadedCount === 1 ? '1 result found.' : `${loadedCount} results found.`;
		}

		return loadedCount === 1 ? '1 more result loaded.' : `${loadedCount} more results loaded.`;
	}

	/**
	 * Move focus to the first newly-loaded card's link, after a keyboard "load more"
	 * activation. Cards are inserted before the button in the DOM, so without this
	 * Tab would skip past the new content entirely (and if the button also hides
	 * itself because there are no pages left, the browser would drop focus to <body>).
	 *
	 * @param {number} previousChildCount Number of cards in the container before this load
	 */
	focusFirstNewCard(previousChildCount) {
		const firstNewCard = this.cardsContainer.children[previousChildCount];
		const focusTarget = firstNewCard?.querySelector('a, button');

		focusTarget?.focus();
	}

	/**
	 * Load additional posts for the post list
	 *
	 * @param {Object} addParams Additional parameters to add to the API request
	 */
	async loadMorePosts(addParams = {}) {
		if (!this.endpoint) {
			throw new Error('No endpoint provided for loadMorePosts');
		}

		const curPage = parseInt(this.cardsContainer.getAttribute('data-current-page'), 10);
		const nextPage = curPage + 1;
		const totalPages = parseInt(this.cardsContainer.getAttribute('data-total-pages'), 10);
		const postsPerPage = parseInt(this.cardsContainer.getAttribute('data-posts-per-page'), 10);

		this.showNoResultsMessage(false);

		if (nextPage > totalPages) {
			this.loadMore.setVisible(false);
			return;
		}

		const params = {
			page: nextPage,
			per_page: postsPerPage,
			...addParams
		};

		const loadMoreButtonHadFocus = this.loadMore?.loadMoreButton === document.activeElement;
		const previousChildCount = this.cardsContainer.children.length;

		this.loadMore.setLoading(true);

		if (this.cardsContainer) {
			this.cardsContainer.setAttribute('aria-busy', 'true');
		}

		try {
			const res = await wpAPIget(this.endpoint, params);

			const newTotalPages =
				typeof res.headers['x-wp-totalpages'] !== 'undefined'
					? parseInt(res.headers['x-wp-totalpages'], 10)
					: totalPages;

			const totalResults =
				typeof res.headers['x-wp-total'] !== 'undefined'
					? parseInt(res.headers['x-wp-total'], 10)
					: null;

			this.showLoadingSpinner(false);

			const loadedCount = res.body.length;

			if (!loadedCount && curPage === 0) {
				this.showNoResultsMessage(true);
			} else {
				const postsHTML = res.body.map((post) => post.card_markup);
				const addMarkup = postsHTML.map((post) => this.postDisplayFunc(post, this));

				this.cardsContainer.insertAdjacentHTML('beforeend', addMarkup.join(''));
			}

			this.announceStatus(this.getStatusMessage(loadedCount, curPage, totalResults));

			this.cardsContainer.setAttribute('data-current-page', nextPage);
			this.cardsContainer.setAttribute('data-total-pages', newTotalPages);

			const hasMorePages = newTotalPages > nextPage;

			this.loadMore.setVisible(hasMorePages);

			// Move focus into the newly-loaded cards so Tab continues through them
			// instead of skipping past (the button sits after the cards in the DOM,
			// so leaving focus on it means Tab never reaches what was just loaded).
			if (loadedCount && loadMoreButtonHadFocus) {
				this.focusFirstNewCard(previousChildCount);
			}

			SiteEvents.publish(SiteEventNames.IMAGEBUDDY_TRIGGER_UPDATE);
		} catch (err) {
			this.showLoadingSpinner(false);
			this.announceStatus('Unable to load results. Please try again.');

			console.error(err);
		}

		if (this.cardsContainer) {
			this.cardsContainer.removeAttribute('aria-busy');
		}

		this.loadMore.setLoading(false);
	}
}

export default PostsList;
